import { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { ChevronsLeftRight, ArrowLeft, ArrowRight } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { imgUrl } from "@/lib/api";

function CompareSlider({ before, after, name, load, hint }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const hinted = useRef(false);

  useEffect(() => {
    if (!inView || !hint || hinted.current) return;
    hinted.current = true;
    const t = setTimeout(() => {
      animate(50, 66, { duration: 0.6, ease: "easeInOut", onUpdate: setPos }).then(() =>
        animate(66, 50, { duration: 0.7, ease: "easeInOut", onUpdate: setPos })
      );
    }, 450);
    return () => clearTimeout(t);
  }, [inView, hint]);

  const move = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  useEffect(() => {
    const onMove = (e) => dragging.current && move(e.clientX);
    const onUp = () => { dragging.current = false; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative h-full w-full overflow-hidden rounded-3xl select-none cursor-ew-resize bg-sand"
      onPointerDown={(e) => {
        e.stopPropagation();
        // Tactile : le glisser horizontal sur la photo fait défiler la galerie
        // (natif) — le comparateur se pilote alors via la poignée centrale.
        if (e.pointerType === "touch") return;
        dragging.current = true; hinted.current = true; move(e.clientX);
      }}
    >
      {load ? (
        <>
          <img src={imgUrl(after)} alt={`Après — ${name}`} loading="lazy" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
            <img src={imgUrl(before)} alt={`Avant — ${name}`} loading="lazy" draggable={false} className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </>
      ) : (
        <div className="absolute inset-0 animate-pulse bg-sand" aria-hidden="true" />
      )}

      {load && (
        <>
          <span className="absolute top-4 left-4 rounded-full bg-ink/60 backdrop-blur-sm text-cream font-mono text-[11px] tracking-[0.2em] px-3.5 py-1.5" data-testid="label-avant">
            AVANT
          </span>
          <span className="absolute top-4 right-4 rounded-full bg-terra text-white font-mono text-[11px] tracking-[0.2em] px-3.5 py-1.5" data-testid="label-apres">
            APRÈS
          </span>
          <div className="absolute top-0 bottom-0 w-0.5 bg-white/90 shadow-[0_0_14px_rgba(0,0,0,0.45)]" style={{ left: `${pos}%` }}>
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 h-12 w-12 rounded-full bg-white shadow-xl grid place-items-center ring-4 ring-white/20"
              style={{ touchAction: "none" }}
              onPointerDown={(e) => { e.stopPropagation(); dragging.current = true; hinted.current = true; move(e.clientX); }}
            >
              <ChevronsLeftRight className="h-5 w-5 text-forest" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SlideInfo({ r }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="mt-5"
      data-testid="realisation-info"
    >
      <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-terra" data-testid="realisation-category">
        {r.category}{r.location ? `  •  ${r.location}` : ""}
      </p>
      <h3 className="mt-1.5 font-display font-semibold text-lg sm:text-xl text-ink" data-testid="realisation-name">{r.name}</h3>
    </motion.div>
  );
}

export default function BeforeAfter({ realizations }) {
  const stripRef = useRef(null);
  const slideRefs = useRef([]);
  const stripDrag = useRef(null);
  const [active, setActive] = useState(0);
  const n = realizations.length;

  const currentIndex = () => {
    const strip = stripRef.current;
    if (!strip || n === 0) return 0;
    const center = strip.scrollLeft + strip.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      const c = el.offsetLeft + el.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
  };

  const updateActive = () => { if (n > 0) setActive(currentIndex()); };

  const goTo = (i) => {
    const idx = Math.min(n - 1, Math.max(0, i));
    slideRefs.current[idx]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  // Molette au-dessus de la galerie : navigue slide par slide. Arrivé à une
  // extrémité, on laisse le scroll vertical de la page reprendre normalement.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || n === 0) return undefined;
    let acc = 0;
    let cooling = false;
    const onWheel = (e) => {
      if (e.ctrlKey) return;
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (d === 0) return;
      const atLeft = strip.scrollLeft <= 1;
      const atRight = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
      if ((d > 0 && atRight) || (d < 0 && atLeft)) return; // extrémité -> la page défile
      e.preventDefault();
      if (cooling) return;
      acc = Math.sign(acc) === Math.sign(d) ? acc + d : d;
      if (Math.abs(acc) >= 60) {
        acc = 0;
        cooling = true;
        goTo(currentIndex() + (d > 0 ? 1 : -1));
        setTimeout(() => { cooling = false; }, 650);
      }
    };
    strip.addEventListener("wheel", onWheel, { passive: false });
    return () => strip.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);

  useEffect(() => {
    const onMove = (e) => {
      if (!stripDrag.current || !stripRef.current) return;
      stripRef.current.scrollLeft = stripDrag.current.left - (e.clientX - stripDrag.current.x);
    };
    const onUp = () => { stripDrag.current = null; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const pad = (x) => String(x).padStart(2, "0");

  return (
    <section id="realisations" data-testid="realisations-section" className="bg-cream text-ink py-16 sm:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <Eyebrow>Réalisations</Eyebrow>
            <h2 className="mt-3 font-display font-semibold tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl max-w-xl" data-testid="realisations-title">
              Des résultats qui se voient.
            </h2>
            <p className="mt-4 text-body2 text-base sm:text-lg max-w-xl">
              Découvrez quelques-unes de nos dernières interventions.
            </p>
          </div>
          {n > 0 && (
            <div className="flex items-center gap-4" data-testid="realisations-nav">
              <span className="font-mono text-sm text-muted2" data-testid="realisations-counter">{pad(active + 1)} / {pad(n)}</span>
              <button onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Réalisation précédente" data-testid="realisations-prev"
                className="h-11 w-11 rounded-full border border-line grid place-items-center text-ink transition-all hover:bg-sand disabled:opacity-30">
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button onClick={() => goTo(active + 1)} disabled={active === n - 1} aria-label="Réalisation suivante" data-testid="realisations-next"
                className="h-11 w-11 rounded-full border border-line grid place-items-center text-ink transition-all hover:bg-sand disabled:opacity-30">
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {n === 0 ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-12 rounded-3xl border border-dashed border-line p-12 text-center text-muted2" data-testid="realisations-empty">
            Les premières réalisations seront publiées ici très prochainement.
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            ref={stripRef}
            onScroll={updateActive}
            onPointerDown={(e) => {
              // Souris uniquement : au tactile, le swipe natif fait défiler la galerie.
              if (e.pointerType !== "mouse") return;
              stripDrag.current = { x: e.clientX, left: stripRef.current.scrollLeft };
            }}
            data-lenis-prevent
            data-testid="realisations-strip"
            className="mt-12 flex gap-5 sm:gap-8 overflow-x-auto snap-x snap-mandatory px-4 sm:px-[10vw] pb-2 cursor-grab active:cursor-grabbing select-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {realizations.map((r, i) => (
              <article
                key={r.id}
                ref={(el) => (slideRefs.current[i] = el)}
                data-testid={`realisation-slide-${i}`}
                className="snap-center shrink-0 w-[88vw] sm:w-[72vw] lg:w-[64vw] max-w-[1100px]"
              >
                <div className="relative aspect-[4/3] sm:aspect-[16/10]">
                  <CompareSlider before={r.before} after={r.after} name={r.name} load={Math.abs(i - active) <= 1} hint={i === 0} />
                </div>
                {i === active && <SlideInfo r={r} />}
              </article>
            ))}
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
            <div className="h-px bg-line relative overflow-hidden rounded-full" data-testid="realisations-progress">
              <motion.div
                className="absolute inset-y-0 left-0 bg-terra"
                animate={{ width: `${((active + 1) / n) * 100}%` }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
}
